<script setup>
import {computed, onMounted, ref, watch} from "vue";
import _, {isNil} from "lodash";
import {usePiniaStore} from "../../store/pinia_store";
import NoGiftAlert from "./NoGiftAlert.vue";
import HasGiftAlert from "./HasGiftAlert.vue";

const props = defineProps({
  dentaidApiInstance: {
    type: Function,
    required: true
  },
  eventBus: {
    type: Object,
    required: true,
  },
  minOrderValue: {
    type: Number,
    required: true,
  },
  hasCartLines: {
    type: Boolean,
    required: true,
  },
  cartTotal: {
    type: Number,
    required: true,
  },
  language: {
    type: String,
    default: 'nl',
  },
});

const emit = defineEmits(['gift-changed'])
const piniaStore = usePiniaStore();

const selectedGift = ref(null);

const hasGifts = computed(() => {
  return !isNil(piniaStore.gifts.lines);
});

const giftsAreApplicableForCustomer = computed(() => {
  return piniaStore?.customer?.customer_group?.cart_gift_applicable;
});

const giftsAreApplicable = computed(() => {
  if (piniaStore?.customer?.is_pharma) {
    return giftsAreApplicableForCustomer.value && hasGifts.value && piniaStore.cart?.conditions?.ship_from_main_location;
  }

  return giftsAreApplicableForCustomer.value && hasGifts.value
});

const canSeeGifts = computed(() => {
  //Check if the cart total is higher than the minimum order value
  if (!props.hasCartLines) {
    return false;
  }

  return props.cartTotal >= props.minOrderValue;
});

const shipFromMainLocation = computed(() => {
  return piniaStore.cart?.conditions?.ship_from_main_location;
})

const handleSelectedGift = (payload) => {
  selectedGift.value = payload;

  props.dentaidApiInstance.post(`/gifts?token=${piniaStore.parsedToken}`, {
    items: {
      "0": {
        sku: payload.sku,
      }
    }
  })
}

const checkInitialGift = () => {
  let gift = _.find(piniaStore.cart.lines, (cartLine) => {
    return cartLine.type === 'Gift';
  });

  if (lodash.isNil(gift)) {
    return;
  }

  selectedGift.value = gift;
}

const currencyFormatter = (value) => {
  return new Intl.NumberFormat('nl-NL', {style: 'currency', currency: 'EUR'}).format(value);
}

watch(selectedGift, () => {
  emit('gift-changed', selectedGift.value);
})

watch(() => piniaStore.gifts.lines, () => {
  if (!isNil(piniaStore.gifts.lines)) {
    selectedGift.value = null;
  }
})

onMounted(() => {
  checkInitialGift();
})
</script>

<template>
  <div class="vue-gift-bar" v-if="giftsAreApplicable">
    <NoGiftAlert
      v-if="!canSeeGifts"
      :min-order-value="minOrderValue"
      :has-cart-lines="hasCartLines"
      :cart-total="cartTotal"
    ></NoGiftAlert>

    <HasGiftAlert
      v-if="canSeeGifts"
      :gifts="piniaStore.gifts.lines"
      :selected-gift="selectedGift"
      @selected-gift="handleSelectedGift"
    ></HasGiftAlert>
  </div>
</template>
